import { supabase } from '../../lib/supabase';
import {
  isMissingPageSetupColumn,
  normalizePageSetup,
  serializePageSetup,
  warnMissingPageSetupColumn,
} from '../../utils/pageSetup';

/**
 * Error thrown when the stored document changed since it was loaded (another tab or a teammate).
 */
export class DocumentConflictError extends Error {
  /**
   * @param {string|null} serverUpdatedAt - The server's current `updated_at`.
   */
  constructor(serverUpdatedAt) {
    super('This document was changed elsewhere since you opened it.');
    this.name = 'DocumentConflictError';
    this.serverUpdatedAt = serverUpdatedAt;
  }
}

/**
 * Conflict-safe save of a generated document's HTML (and, when given, its page setup). The write
 * only applies when the row's `updated_at` still equals `expectedUpdatedAt` (optimistic
 * concurrency); pass `force: true` to overwrite after the user chose to. This is the single DB
 * write for an editor save: opener windows only refresh their lists from the DOCUMENT_UPDATED
 * message.
 *
 * `pageSetup`: omit (undefined) to leave the stored page_setup alone; pass null or an object to
 * write it (objects are normalized first). Content and page setup go in the same UPDATE, so the
 * conflict check covers both. When the database does not have the page_setup column yet, the
 * write is retried once without it (the content still saves; `pageSetupSaved` is false).
 *
 * @param {{documentId: string, content: string, pageSetup?: object|null, expectedUpdatedAt?: string|null, force?: boolean}} input - Save input.
 * @returns {Promise<{updatedAt: string|null, pageSetupSaved: boolean}>} The new `updated_at`, and whether page_setup was written.
 * @throws {DocumentConflictError} When the row changed since `expectedUpdatedAt`.
 */
export async function saveDocumentContent({ documentId, content, pageSetup, expectedUpdatedAt = null, force = false }) {
  if (!documentId) throw new Error('No document id');
  const updatedAt = new Date().toISOString();
  const patch = { content, updated_at: updatedAt };
  if (pageSetup !== undefined) patch.page_setup = pageSetup === null ? null : normalizePageSetup(pageSetup);
  const run = (values) => {
    let query = supabase
      .from('generated_documents')
      .update(values)
      .eq('id', documentId);
    if (!force && expectedUpdatedAt) query = query.eq('updated_at', expectedUpdatedAt);
    return query.select('id, updated_at');
  };
  let pageSetupSaved = 'page_setup' in patch;
  let { data, error } = await run(patch);
  if (error && pageSetupSaved && isMissingPageSetupColumn(error)) {
    warnMissingPageSetupColumn();
    const { page_setup: _omit, ...withoutPageSetup } = patch;
    pageSetupSaved = false;
    ({ data, error } = await run(withoutPageSetup));
  }
  if (error) throw error;
  if (Array.isArray(data) && data.length > 0) {
    return { updatedAt: data[0].updated_at || updatedAt, pageSetupSaved };
  }
  // Zero rows: either the document changed elsewhere or it is not writable for this user.
  const { data: current, error: readError } = await supabase
    .from('generated_documents')
    .select('id, updated_at')
    .eq('id', documentId)
    .maybeSingle();
  if (readError) throw readError;
  if (!current) throw new Error('You no longer have access to this document.');
  if (!force && expectedUpdatedAt && current.updated_at !== expectedUpdatedAt) {
    throw new DocumentConflictError(current.updated_at || null);
  }
  throw new Error('The document could not be saved. You may not have permission to edit it.');
}

/**
 * Tell the window that opened this editor (if any) that the document changed, so its lists
 * can refresh. Uses the app's own origin as target (never '*').
 * @param {{documentId: string, updatedAt: string|null, content?: string, title?: string, pageSetup?: object|null}} data - Message payload (pageSetup only when that save wrote it).
 * @returns {void}
 */
export function notifyOpenerDocumentUpdated(data) {
  try {
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({ type: 'DOCUMENT_UPDATED', data }, window.location.origin);
    }
  } catch {
    /* opener may be cross-origin or gone */
  }
}

/** Newline between the page setup JSON (which never contains a raw newline) and the HTML. */
const SNAPSHOT_SEPARATOR = String.fromCharCode(10);

/**
 * Pack a document's saved parts into the single string the autosave baseline compares: the
 * page setup's stable JSON (no raw newlines), a newline, then the HTML. Page-setup-only changes
 * therefore make the document dirty too.
 * @param {string} html - Document HTML.
 * @param {unknown} pageSetup - Page setup (normalized first; null = defaults).
 * @returns {string} Snapshot.
 */
export function packDocumentSnapshot(html, pageSetup) {
  return `${serializePageSetup(pageSetup)}${SNAPSHOT_SEPARATOR}${html}`;
}

/**
 * Inverse of packDocumentSnapshot. A string without the page setup line (an older caller passing
 * bare HTML) is returned as the HTML with a null page setup.
 * @param {string} snapshot - Snapshot.
 * @returns {{html: string, pageSetup: object|null, pageSetupKey: string|null}} Parts (pageSetup normalized).
 */
export function unpackDocumentSnapshot(snapshot) {
  const text = typeof snapshot === 'string' ? snapshot : '';
  const newline = text.indexOf(SNAPSHOT_SEPARATOR);
  if (text.startsWith('{"version":') && newline > 0) {
    const pageSetupKey = text.slice(0, newline);
    try {
      return { html: text.slice(newline + 1), pageSetup: normalizePageSetup(JSON.parse(pageSetupKey)), pageSetupKey };
    } catch {
      /* not a packed snapshot */
    }
  }
  return { html: text, pageSetup: null, pageSetupKey: null };
}
